// modules/categories/ui.ts — <app-panel-categorias>

import { getModule } from '../../core/registry';
import { useBus }    from '../../shared/hooks/use-bus';
import type { CategoryMap } from '../../shared/types/global';

export class AppPanelCategorias extends HTMLElement {
  private _draft: CategoryMap = {};

  static define() { customElements.define('app-panel-categorias', AppPanelCategorias); }

  connectedCallback() {
    useBus('categories:updated', ({ categories }) => {
      this._draft = JSON.parse(JSON.stringify(categories));
      this.renderList();
    }, this);

    this._draft = JSON.parse(JSON.stringify(
      getModule<{ getAll: () => CategoryMap }>('categories')?.getAll() ?? {}
    ));
    this.render();
  }

  private render() {
    this.innerHTML = `
      <style>
        h2 { font-family:var(--font-display);font-size:var(--text-xl);margin-bottom:var(--space-2); }
        p.sub { font-size:var(--text-xs);color:var(--color-text-muted);margin-bottom:var(--space-4); }
        .cat-card { background:var(--color-surface);border:1px solid var(--color-border);
                    border-radius:var(--radius-lg);padding:var(--space-4);margin-bottom:var(--space-3); }
        .cat-row  { display:flex;gap:var(--space-2);align-items:center;margin-bottom:var(--space-3); }
        .cat-input,.sub-input {
          flex:1;background:var(--color-bg);border:1px solid var(--color-border);
          border-radius:var(--radius-md);padding:var(--space-2) var(--space-3);
          color:var(--color-text);font-family:var(--font-body);font-size:var(--text-sm);
        }
        .cat-input { font-weight:var(--font-weight-semibold); }
        .sub-row   { display:flex;gap:var(--space-2);align-items:center;margin-bottom:var(--space-2);margin-left:var(--space-4); }
        .btn-sm    { background:var(--color-surface-raised);border:1px solid var(--color-border);
                     border-radius:var(--radius-md);padding:var(--space-1) var(--space-3);
                     font-family:var(--font-body);font-size:var(--text-xs);cursor:pointer;color:var(--color-text-muted); }
        .btn-icon  { background:none;border:none;cursor:pointer;color:var(--color-text-muted);font-size:1rem;padding:var(--space-1); }
        .btn-save  { width:100%;background:var(--color-primary);color:white;border:none;
                     border-radius:var(--radius-md);padding:var(--space-3);
                     font-family:var(--font-body);font-weight:var(--font-weight-semibold);cursor:pointer; }
        .btn-add   { width:100%;margin-bottom:var(--space-3);background:var(--color-surface);
                     border:1px dashed var(--color-border);border-radius:var(--radius-md);
                     padding:var(--space-3);cursor:pointer;color:var(--color-text-muted);font-family:var(--font-body); }
      </style>
      <h2>Categorías</h2>
      <p class="sub">La IA usará esta estructura para clasificar tus movimientos automáticamente.</p>
      <div id="catList"></div>
      <button class="btn-add" id="btnAddCat">+ Agregar categoría</button>
      <button class="btn-save" id="btnSaveCats">Guardar categorías</button>
    `;

    this.renderList();

    this.querySelector('#btnAddCat')!.addEventListener('click', () => {
      let name = 'Nueva categoría';
      let i = 1;
      while (this._draft[name]) name = `Nueva categoría ${i++}`;
      this._draft[name] = ['Otros'];
      this.renderList();
    });

    this.querySelector('#btnSaveCats')!.addEventListener('click', async () => {
      const btn = this.querySelector<HTMLButtonElement>('#btnSaveCats')!;
      btn.disabled    = true;
      btn.textContent = 'Guardando…';
      await getModule<{ save: (c: CategoryMap) => Promise<void> }>('categories')?.save(this._draft);
      btn.disabled    = false;
      btn.textContent = 'Guardar categorías';
    });
  }

  private renderList() {
    const list = this.querySelector('#catList')!;
    list.innerHTML = Object.entries(this._draft).map(([cat, subs]) => `
      <div class="cat-card" data-cat="${cat}">
        <div class="cat-row">
          <input class="cat-input" value="${cat}" data-old="${cat}">
          <button class="btn-icon del-cat" data-cat="${cat}">🗑️</button>
        </div>
        <div class="sub-list">
          ${(subs as string[]).map((sub: string, i: number) => `
            <div class="sub-row">
              <span style="color:var(--color-text-muted);font-size:1rem">↳</span>
              <input class="sub-input" value="${sub}" data-cat="${cat}" data-idx="${i}">
              <button class="btn-icon del-sub" data-cat="${cat}" data-idx="${i}">✕</button>
            </div>
          `).join('')}
        </div>
        <button class="btn-sm add-sub" data-cat="${cat}">+ Subcategoría</button>
      </div>
    `).join('');

    list.querySelectorAll<HTMLInputElement>('.cat-input').forEach(el => {
      el.addEventListener('change', () => {
        const old = el.dataset.old!;
        const nw  = el.value.trim();
        if (nw && nw !== old && !this._draft[nw]) {
          this._draft[nw] = this._draft[old];
          delete this._draft[old];
          this.renderList();
        } else { el.value = old; }
      });
    });

    list.querySelectorAll<HTMLInputElement>('.sub-input').forEach(el => {
      el.addEventListener('change', () => {
        const cat = el.dataset.cat!;
        const idx = parseInt(el.dataset.idx!);
        const val = el.value.trim();
        if (val) this._draft[cat][idx] = val;
      });
    });

    list.querySelectorAll<HTMLButtonElement>('.del-cat').forEach(btn => {
      btn.addEventListener('click', () => { delete this._draft[btn.dataset.cat!]; this.renderList(); });
    });

    list.querySelectorAll<HTMLButtonElement>('.del-sub').forEach(btn => {
      btn.addEventListener('click', () => {
        this._draft[btn.dataset.cat!].splice(parseInt(btn.dataset.idx!), 1);
        this.renderList();
      });
    });

    list.querySelectorAll<HTMLButtonElement>('.add-sub').forEach(btn => {
      btn.addEventListener('click', () => { this._draft[btn.dataset.cat!].push('Nueva sub'); this.renderList(); });
    });
  }
}
